import { ref, computed, watch, nextTick, onMounted } from 'vue'

const DANISH_NUMBER_REGEX = /^(?:\d{1,3}(?:\.\d{3})*|\d+)(?:,\d{1,2})?$/

function isValidDanishNumber(value) {
    if (!value) return false
    return DANISH_NUMBER_REGEX.test(String(value).trim())
}

function parseDanishNumberToFloat(value) {
    if (!isValidDanishNumber(value)) return null
    const cleaned = String(value).replace(/\./g, '').replace(',', '.')
    const n = Number(cleaned)
    return Number.isFinite(n) ? n : null
}

export default {
    name: 'DonationAmountPicker',
    props: {
        donationAmounts: {
            type: Array,
            required: false,
            default: () => []
        },
        minimumAmount: {
            type: Number,
            default: 1
        },
        onAmountChangeCallback: {
            type: Function,
            required: true
        },
        displayInputError: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: ''
        },
        initialValue: {
            type: Number,
            default: null
        },
        customDonationButtonLabel: {
            type: String,
            default: 'Eller donér et andet beløb'
        },
        customAmountOnly: {
            type: Boolean,
            default: false
        },
        validationCustomDonationFormatLabel: {
            type: String,
            default: 'Indtast venligst et gyldigt beløb'
        },
        validationCustomDonationAmountLabel: {
            type: String,
            default: 'Beløbet er for lavt'
        },
        type: {
            type: String,
            default: 'default'
        }
    },
    setup(props) {
        const middleAmount = props.donationAmounts.length > 0
            ? props.donationAmounts[Math.floor((props.donationAmounts.length - 1) / 2)]
            : null
        const selectedAmount = ref(props.initialValue ?? middleAmount)
        const customDonation = ref(false)
        const customDonationAmountInput = ref('')
        const customDonationAmountTouched = ref(false)
        const valueinputfield = ref(null)

        onMounted(() => {
            if (props.customAmountOnly) {
                customDonation.value = true
                return
            }

            if (selectedAmount.value != null) {
                props.onAmountChangeCallback(selectedAmount.value)
            }
        })

        watch(() => props.initialValue, (newVal) => {
            if (newVal != null) {
                selectedAmount.value = newVal
            }
        })

        const customDonationAmountFormatIsValid = computed(() => {
            if (!customDonation.value) return true
            const raw = customDonationAmountInput.value?.toString().trim() ?? ''
            return isValidDanishNumber(raw)
        })

        const customDonationAmountMinIsValid = computed(() => {
            if (!customDonation.value) return true
            const raw = customDonationAmountInput.value?.toString().trim() ?? ''
            if (!isValidDanishNumber(raw)) return true
            const num = parseDanishNumberToFloat(raw)
            return num !== null && num >= props.minimumAmount
        })

        const validateCustomDonationOnBlur = () => {
            customDonationAmountTouched.value = true
            if (customDonationAmountFormatIsValid.value && customDonationAmountMinIsValid.value) {
                const parsed = parseDanishNumberToFloat(customDonationAmountInput.value)
                props.onAmountChangeCallback(parsed)
            } else {
                props.onAmountChangeCallback(null)
            }
        }

        const disableCustomDonation = () => {
            customDonation.value = false
            customDonationAmountInput.value = ''
            customDonationAmountTouched.value = false
        }

        const enableCustomDonation = async () => {
            selectedAmount.value = null
            props.onAmountChangeCallback(null)
            await nextTick()
            valueinputfield.value?.focus()
        }

        const toggleCustomDonation = async () => {
            customDonation.value = !customDonation.value
            if (customDonation.value) {
                await enableCustomDonation()
            } else {
                disableCustomDonation()
                if (props.donationAmounts.length > 0) {
                    selectedAmount.value = props.donationAmounts[0]
                    props.onAmountChangeCallback(selectedAmount.value)
                }
            }
        }

        const onAmountChange = () => {
            disableCustomDonation()
            props.onAmountChangeCallback(selectedAmount.value)
        }

        return {
            selectedAmount,
            customDonation,
            customDonationAmountInput,
            customDonationAmountTouched,
            customDonationAmountFormatIsValid,
            customDonationAmountMinIsValid,
            valueinputfield,
            validateCustomDonationOnBlur,
            disableCustomDonation,
            toggleCustomDonation,
            onAmountChange,
            customAmountOnly: props.customAmountOnly,
            type: props.type
        }
    }
}
